import { useEffect, useState } from 'react';
import { useSsoStore } from '../../store/ssoStore';
import { useUserStore } from '../../../auth/store/userStore';
import { showToast } from '@shared/services/toast';
import { updateSsoConfiguration } from '../../services/ssoService';
import { FirmSubdomainSection } from '../index';
import { Button, DropdownOptions } from '@shared/ui';
import { sectionHeading, sectionDescription, saveRow, saveButton } from '../profileStyles';

/**
 * SSO Section - Manage Company Domain and Authentication Providers for Single Sign-On
 * Moved from SignUpPage to ProfileModal
 * Follows the same pattern as NotificationSection with store-based state management
 */
export function SSOSection({ onLoadingChange }) {
  const [saving, setSaving] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [subdomain, setSubdomain] = useState(null);
  const [subdomainStatus, setSubdomainStatus] = useState(null);
  const [subdomainError, setSubdomainError] = useState(null);

  const { profile, updateProfile } = useUserStore();
  const {
    providers,
    configuration,
    hasLoaded,
    configurationError,
    isLoadingProviders,
    isLoadingConfiguration,
    getSsoProviders,
    getSsoConfiguration,
    reset: resetSsoStore
  } = useSsoStore();

  // Combined loading state: show loading if either providers or configuration is loading
  const isLoading = isLoadingProviders || isLoadingConfiguration;

  // Load SSO providers and configuration on mount
  useEffect(() => {
    getSsoProviders();
    getSsoConfiguration();

    // Cleanup: clear SSO store errors on unmount to prevent stale errors on re-open
    return () => {
      resetSsoStore();
    };
  }, [getSsoProviders, getSsoConfiguration, resetSsoStore]);

  // Bind configuration to component state only when successfully loaded (M3 - gate on hasLoaded)
  useEffect(() => {
    if (hasLoaded && configuration && !configurationError) {
      setSelectedProviderId(configuration.externalAuthenticationProviderId ?? null);
      setSubdomain(configuration.subdomain ?? null);
    }
  }, [hasLoaded, configuration, configurationError]);

  const handleSaveFailure = (result) => {
    // M1 - Handle 409 (subdomain conflict) specially
    if (result.status === 409) {
      setSubdomainError(result.message || 'This domain is already taken');
      showToast.error(result.message || 'Domain conflict: this subdomain is already in use');
      return;
    }
    // M1 - Handle 400 field errors
    if (result.status === 400 && result.fieldErrors?.subdomain) {
      setSubdomainError(result.fieldErrors.subdomain);
      showToast.error(result.fieldErrors.subdomain);
      return;
    }
    showToast.error(result.message || 'Failed to save SSO configuration');
  };

  const handleSaveChanges = async () => {
    // M5 - Guard: do not send subdomain if configuration fetch failed
    if (configurationError || isLoadingConfiguration) {
      showToast.error('Please wait for configuration to load before saving');
      return;
    }

    setSaving(true);
    onLoadingChange?.(true, 'Saving SSO configuration...');
    try {
      const selectedProvider = providers.find(p => p.id === selectedProviderId);

      const result = await updateSsoConfiguration({
        externalAuthenticationProviderId: selectedProviderId || null,
        idpAlias: selectedProvider?.code || null,
        subdomain: subdomain || null
      });

      if (!result.success) {
        handleSaveFailure(result);
        return;
      }

      // Update user store to keep profile.firm.subdomain in sync
      if (subdomain !== undefined && subdomain !== null) {
        updateProfile({ firm: { ...profile.firm, subdomain } });
      }

      showToast.success('SSO configuration saved successfully!');
    } catch (err) {
      showToast.error(err.message || 'Failed to save SSO configuration');
    } finally {
      setSaving(false);
      onLoadingChange?.(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-sso-section">
      {/* Header */}
      <div>
        <h3 className={sectionHeading}>Single Sign-On (SSO)</h3>
        <p className={`${sectionDescription} mt-1`}>
          Configure your company's domain and authentication providers for SSO access
        </p>
      </div>

      {/* Authentication Provider Dropdown */}
      <div className="space-y-1.5">
        {/* Shared DropdownOptions (as Country / Main Contact) rather than a native <select>, so the
            field, its label and its opened list match every other dropdown in the app. `allowEmpty`
            keeps the old "no provider" choice. */}
        <DropdownOptions
          id="sso-provider"
          label="Authentication Provider"
          // Blank while loading: a saved id has no option to match yet, which would leave the trigger
          // empty instead of showing "Loading...".
          value={isLoading || selectedProviderId == null ? '' : String(selectedProviderId)}
          onValueChange={(value) => setSelectedProviderId(value ? Number.parseInt(value, 10) : null)}
          options={providers.map((provider) => ({ value: String(provider.id), label: provider.name }))}
          loading={isLoading}
          placeholder="Select a provider"
          allowEmpty
          emptyLabel="No provider"
          placeholderWhenEmpty
          dataTestId="profile-sso-provider"
        />
        <p className="text-xs text-fg-muted">
          Choose the authentication provider for your organization's SSO configuration
        </p>
      </div>

      {/* Company Domain Field - from FirmSubdomainSection (only show when provider is selected) */}
      {selectedProviderId && (
        <FirmSubdomainSection 
          onSubdomainChange={setSubdomain}
          initialValue={subdomain}
          onStatusChange={setSubdomainStatus}
          onErrorChange={setSubdomainError}
        />
      )}

      {/* Save Changes Button */}
      <div className={saveRow}>
        <Button
          type="button"
          onClick={handleSaveChanges}
          disabled={saving || isLoading || subdomainStatus === 'checking' || subdomainStatus === 'taken' || !!subdomainError || configurationError || isLoadingConfiguration}
          className={saveButton}
          data-testid="profile-sso-save-button"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
